import {DEFAULT_ERROR} from './errors';

import type {z} from 'zod';

const STRING_FORMAT_MESSAGES: Partial<Record<string, string>> = {
  email: 'Please enter a valid email address.',
  url: 'Please enter a valid URL.',
  uuid: 'This value is not a valid ID.',
  guid: 'This value is not a valid ID.',
  date: 'Please enter a valid date.',
  time: 'Please enter a valid time.',
  datetime: 'Please enter a valid date and time.',
  ipv4: 'Please enter a valid IP address.',
  ipv6: 'Please enter a valid IP address.',
};

/**
 * Friendly validation messages for Formik/Zod forms. Scoped per-parse-call
 * via `schema.safeParse(values, {error: formErrorMap})` — never installed
 * globally via `z.config()`, so it never affects API-response schemas.
 *
 * Issue shapes (`origin`, `values`, `format`, etc. rather than the `type`/
 * `validation` fields an earlier draft of this map assumed) are Zod 4.4.3's
 * actual `$ZodIssue` union — see node_modules/zod/v4/core/errors.d.ts.
 */
export const formErrorMap: z.core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case 'invalid_type':
      return typeof issue.input === 'undefined'
        ? 'This field is required.'
        : 'This value is not valid.';

    case 'invalid_format':
      return STRING_FORMAT_MESSAGES[issue.format] ?? 'This value is not valid.';

    case 'invalid_value': {
      const values = issue.values;
      return values.length === 1
        ? `Must be "${String(values[0])}".`
        : `Must be one of: ${values.map(String).join(', ')}.`;
    }

    case 'too_small':
      if (issue.origin === 'array' || issue.origin === 'set')
        return `Please add ${
          issue.exact ? 'exactly' : issue.inclusive ? 'at least' : 'more than'
        } ${issue.minimum} item(s).`;
      else if (issue.origin === 'string')
        return `Please enter ${
          issue.exact ? 'exactly' : issue.inclusive ? 'at least' : 'more than'
        } ${issue.minimum} character(s).`;
      else if (
        issue.origin === 'number' ||
        issue.origin === 'int' ||
        issue.origin === 'bigint'
      )
        return `Please enter a number ${
          issue.exact
            ? 'equal to'
            : issue.inclusive
              ? 'greater than or equal to'
              : 'greater than'
        } ${issue.minimum}.`;
      else if (issue.origin === 'date')
        return `Please choose a date ${
          issue.exact ? 'equal to' : issue.inclusive ? 'on or after' : 'after'
        } ${new Date(Number(issue.minimum)).toLocaleDateString()}.`;
      return 'This value is not valid.';

    case 'too_big':
      if (issue.origin === 'array' || issue.origin === 'set')
        return `Please add ${
          issue.exact ? 'exactly' : issue.inclusive ? 'at most' : 'fewer than'
        } ${issue.maximum} item(s).`;
      else if (issue.origin === 'string')
        return `Please enter ${
          issue.exact ? 'exactly' : issue.inclusive ? 'at most' : 'fewer than'
        } ${issue.maximum} character(s).`;
      else if (
        issue.origin === 'number' ||
        issue.origin === 'int' ||
        issue.origin === 'bigint'
      )
        return `Please enter a number ${
          issue.exact
            ? 'equal to'
            : issue.inclusive
              ? 'less than or equal to'
              : 'less than'
        } ${issue.maximum}.`;
      else if (issue.origin === 'date')
        return `Please choose a date ${
          issue.exact ? 'equal to' : issue.inclusive ? 'on or before' : 'before'
        } ${new Date(Number(issue.maximum)).toLocaleDateString()}.`;
      return 'This value is not valid.';

    case 'not_multiple_of':
      return `Must be a multiple of ${issue.divisor}.`;

    case 'custom':
      return 'This value is not valid.';

    case 'unrecognized_keys':
    case 'invalid_union':
    case 'invalid_element':
    case 'invalid_key':
    default:
      return DEFAULT_ERROR;
  }
};
